import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    errorMessage: string;
    folderLimitExceededTitle: string;
    concurrentBatchLimitExceededTitle: string;
    concurrentBatchLimitExceededDetails: (maxConcurrentBatchCount: number) => string;
    concurrentFileLimitExceededTitle: string;
    concurrentFileLimitExceededDetails: (currentFileCount: number, maxLimit: number) => string;
    folderDepthLimitExceededTitle: string;
    folderDepthLimitExceededDetails: (maxFolderDepth: number) => string;
    folderLimitExceededDetails: (maxFolderCount: number) => string;
    fileLimitExceededTitle: string;
    fileLimitExceededDetails: (maxFileCount: number) => string;
    emptyFoldersNotSupported: string;
    esmTreeUploadTimeoutTitle: string;
    esmTreeUploadTimeoutDetails: string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Generic error message when upload fails */
            errorMessage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.errorMessage?c=8tsgdxxhsa9r", defaultMessage: "Your assets could not be uploaded." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title for folder limit exceeded error */
            folderLimitExceededTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.folderLimitExceededTitle?c=3s3zds76gl4b1", defaultMessage: "Upload Limit Reached" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title for concurrent batch limit exceeded error */
            concurrentBatchLimitExceededTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.concurrentBatchLimitExceededTitle?c=2gdty7uu48dd", defaultMessage: "You''ve reached your concurrent upload limit" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Details for concurrent batch limit exceeded error */
            concurrentBatchLimitExceededDetails: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (maxConcurrentBatchCount: number): string => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.concurrentBatchLimitExceededDetails?c=2x2ac12k7v6gp", defaultMessage: "{maxConcurrentBatchCount, plural, one {Frame.io currently supports 1 simultaneous upload. Cancel an upload, or wait for it to complete.} other {Frame.io currently supports {maxConcurrentBatchCount, number} simultaneous uploads. Cancel an upload, or wait for some to complete.}}" }, { maxConcurrentBatchCount: maxConcurrentBatchCount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title for concurrent file limit exceeded error */
            concurrentFileLimitExceededTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.concurrentFileLimitExceededTitle?c=2wtd4b4dpkbmq", defaultMessage: "Upload Queue Limit Reached" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Details for concurrent file limit exceeded error */
            concurrentFileLimitExceededDetails: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (currentFileCount: number, maxLimit: number): string => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.concurrentFileLimitExceededDetails?c=30o9j3nv1eni9", defaultMessage: "You''ve selected {currentFileCount, plural, one {1 file} other {{currentFileCount, number} files}}, but the maximum is {maxLimit, number}. Please select fewer files and try again." }, { currentFileCount: currentFileCount, maxLimit: maxLimit }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title for folder depth limit exceeded error */
            folderDepthLimitExceededTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.folderDepthLimitExceededTitle?c=2yeafykuqmia3", defaultMessage: "Your upload''s folder structure is too deep" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Details for folder depth limit exceeded error */
            folderDepthLimitExceededDetails: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (maxFolderDepth: number): string => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.folderDepthLimitExceededDetails?c=2ovikv2w5ye29", defaultMessage: "{maxFolderDepth, plural, one {Frame.io currently supports uploading folders nested up to 1 level deep.} other {Frame.io currently supports uploading folders nested up to {maxFolderDepth, number} levels deep.}}" }, { maxFolderDepth: maxFolderDepth }));
                },
                enumerable: true,
                configurable: true
            },
            /** Details for folder limit exceeded error */
            folderLimitExceededDetails: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (maxFolderCount: number): string => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.folderLimitExceededDetails?c=374r72us9c36n", defaultMessage: "{maxFolderCount, plural, one {Please select 1 or fewer folders to upload, then try again.} other {Please select {maxFolderCount, number} or fewer folders to upload, then try again.}}" }, { maxFolderCount: maxFolderCount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title for file limit exceeded error */
            fileLimitExceededTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.fileLimitExceededTitle?c=3s3zds76gl4b1", defaultMessage: "Upload Limit Reached" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Details for file limit exceeded error */
            fileLimitExceededDetails: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (maxFileCount: number): string => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.fileLimitExceededDetails?c=3pwjntkmouzqk", defaultMessage: "{maxFileCount, plural, one {Please select 1 or fewer assets to upload, then try again.} other {Please select {maxFileCount, number} or fewer assets to upload, then try again.}}" }, { maxFileCount: maxFileCount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error message when empty folders are selected */
            emptyFoldersNotSupported: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.emptyFoldersNotSupported?c=33ocfogxttwn5", defaultMessage: "Empty folders are not supported via file select" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Title shown when direct-to-ESM upload URLs do not arrive in time */
            esmTreeUploadTimeoutTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.esmTreeUploadTimeoutTitle?c=65pz4a3rktot", defaultMessage: "Upload preparation timed out" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Details shown when direct-to-ESM upload URLs do not arrive in time */
            esmTreeUploadTimeoutDetails: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useUploadWorkerQueue.esmTreeUploadTimeoutDetails?c=1cc3pwr00kmul", defaultMessage: "We could not finish preparing your upload. Please try again." }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
